import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimesCircle, FaFilePdf } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Expired.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://digitalapi.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

/** Smart page-number array with ellipses */
const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Expired() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [expired, setExpired] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Fetch expired policies
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/getExpired?agent=${agentCode}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setExpired(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('getExpired error', e);
        toast.error('Failed to load expired policies');
        setExpired([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FILTERED DATA (memoized client-side)
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return expired;
    const q = searchTerm.toLowerCase();
    return expired.filter(v =>
      (v.PolicyNo || '').toLowerCase().includes(q) ||
      (v.Dealer_name || '').toLowerCase().includes(q) ||
      (v.Narration || '').toLowerCase().includes(q) ||
      (v.ProductDescription || '').toLowerCase().includes(q) ||
      (v.Mobile || '').toLowerCase().includes(q)
    );
  }, [expired, searchTerm]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // ══════════════════════════════════════
  //  PAGINATED DATA
  // ══════════════════════════════════════
  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const indexOfLast = indexOfFirst + ROWS_PER_PAGE;
  const currentRows = filtered.slice(indexOfFirst, indexOfLast);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  PDF EXPORT (jspdf-autotable, no DOM mutation)
  // ══════════════════════════════════════
  const exportToPDF = useCallback(() => {
    if (filtered.length === 0) {
      toast.warn('No data to export');
      return;
    }
    setExporting(true);
    try {
      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.setFontSize(16);
      pdf.setTextColor(26, 92, 90);
      pdf.text('Expired Policies', 14, 16);
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(`Generated: ${new Date().toLocaleDateString()} | Records: ${filtered.length}`, 14, 22);

      pdf.autoTable({
        startY: 28,
        head: [['#', 'Policy No.', 'Insured', 'Contact', 'Registration', 'Product', 'Expiry Date']],
        body: filtered.map((v, i) => [
          i + 1,
          v.PolicyNo || '--',
          v.Dealer_name || '--',
          v.Mobile || '--',
          v.Narration || '--',
          v.ProductDescription || '--',
          formatISODate(v.ExpiryDate),
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [26, 92, 90], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 248, 247] },
      });

      pdf.save('expired_policies.pdf');
      toast.success('PDF exported');
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  }, [filtered]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="expired-page">
      <Sidebar />
      <div className="expired-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="expired-top-row">
          <span className="expired-company">Definite Assurance</span>
          <span className="expired-breadcrumb">Dashboard / Renewals / Expired</span>
        </div>

        <div className="expired-card">
          <h3><FaTimesCircle style={{ marginRight: 8 }} /> Expired Policies</h3>

          {/* Toolbar: search + export + stat */}
          <div className="expired-toolbar">
            <div className="expired-search-wrap">
              <FaSearch className="expired-search-icon" />
              <input
                type="text"
                placeholder="Search by policy, client, registration, product…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              className="expired-export-btn"
              onClick={exportToPDF}
              disabled={exporting || filtered.length === 0}
            >
              <FaFilePdf />
              {exporting ? 'Exporting…' : 'Export PDF'}
            </button>

            <span className="expired-stat">
              Total: <strong>{expired.length}</strong>
              {filtered.length !== expired.length && (
                <> &nbsp;|&nbsp; Found: <strong>{filtered.length}</strong></>
              )}
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="expired-loading">
              <div className="expired-spinner" />
              <p>Loading expired policies…</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="expired-empty">
              <FaTimesCircle size={32} color="#ccc" style={{ marginBottom: 10 }} />
              <p>{searchTerm ? 'No expired policies match your search.' : 'No expired policies found.'}</p>
            </div>
          )}

          {/* Table */}
          {!loading && filtered.length > 0 && (
            <>
              <div className="expired-table-wrap">
                <table className="expired-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Policy No.</th>
                      <th>Insured</th>
                      <th>Contact</th>
                      <th>Registration</th>
                      <th>Product</th>
                      <th>Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, index) => (
                      <tr key={val.OID || index}>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>{val.PolicyNo}</td>
                        <td>{val.Dealer_name}</td>
                        <td>{val.Mobile}</td>
                        <td>{val.Narration}</td>
                        <td>{val.ProductDescription}</td>
                        <td>
                          <span className="expired-badge">
                            {formatISODate(val.ExpiryDate)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="expired-pagination">
                <span className="expired-pagination-info">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filtered.length)} of {filtered.length} entries
                </span>
                <div className="expired-pagination-controls">
                  <button
                    className="expired-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers.map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="expired-page-ellipsis">…</span>
                      : (
                        <button
                          key={page}
                          className={`expired-page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                  )}
                  <button
                    className="expired-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
